import { Verified } from "lucide-react";
import React from "react";
import { useTranslations } from "next-intl";

import { cn } from "@/shared/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/ui/overlays/tooltip";

interface VerifiedBadgeProps {
  isVerified: boolean;
  reason?: string | null;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const sizeClasses = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
};

const VerifiedBadge = React.memo(
  ({ isVerified, reason, className, size = "md" }: VerifiedBadgeProps) => {
    const t = useTranslations("Common");
    if (!isVerified) return null;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block cursor-help">
            <Verified
              className={cn(
                "text-primary hover:opacity-90 transition-colors",
                sizeClasses[size],
                className,
              )}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1">
            <p>{t("verifiedBadgeTitle")}</p>
            {reason ? (
              <p className="max-w-[320px] text-muted-foreground whitespace-pre-wrap wrap-break-word">
                {t("verifiedBadgeReason", { reason })}
              </p>
            ) : null}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  },
);

VerifiedBadge.displayName = "VerifiedBadge";

export default VerifiedBadge;
